import { useState, useCallback, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";

import { tableActionsCreator } from "app/actions/tableActions";
import { createKeywordGroupEntity } from "app/modules/demand-analysis/keyword-lists";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { i18nFilter } from "app/filters/i18nFilter";
import AddTableRowsKeywordsToGroupUtility from "app/pages/keyword-analysis/AddTableRowsKeywordsToGroupUtility";
import { resolveAllowedCountry } from "app/pages/market-research/shared/countryResolution";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { DesktopMobileShare, getColumns } from "./getColumns";
import { getInitialFilters } from "./getInitialFilters";
import { TableTop } from "./TableTop";
import { transformData } from "./transformData";

const TABLE_SELECTION_KEY = "INDUSTRY_TRENDS_TABLE";
const TABLE_SELECTION_PROPERTY = "SearchTerm";
const TABLE_ENDPOINT = "/widgetApi/IndustryAnalysisTopKeywords/SearchKeywordsAbb/Table";

const i18n = i18nFilter();

export const SearchTrendsTable = () => {
    const { params } = useCurrentStateAndParams();
    const { webSource, country, duration, category } = params;
    const [hiddenColumns, setHiddenColumns] = useState(new Set<string>());
    const [sort, setSort] = useState<{ field: string; sortDirection: "desc" | "asc" }>({
        field: "TotalShare",
        sortDirection: "desc",
    });

    const onSort = ({ field, sortDirection }: { field: string; sortDirection: "desc" | "asc" }) => {
        setSort({ field, sortDirection });
    };

    const handleColumnToggle = (fieldName: string) => {
        const updatedHiddenColumns = new Set(hiddenColumns);

        if (updatedHiddenColumns.has(fieldName)) {
            updatedHiddenColumns.delete(fieldName);
        } else {
            updatedHiddenColumns.add(fieldName);
        }
        setHiddenColumns(updatedHiddenColumns);
    };

    const handleViewKeywordGroupClick = useCallback(
        (keywordGroup: string) => {
            try {
                const resolvedCountry = resolveAllowedCountry(
                    KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                    country,
                );

                const keywordGroupEntity = createKeywordGroupEntity(
                    { type: "KeywordGroup", keywordGroupId: keywordGroup },
                    keywordGroup,
                );

                void proNavigator.go(
                    KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                    {
                        id: [keywordGroupEntity.keywordListId],
                        duration,
                        country: resolvedCountry,
                        ignoreFilterConsistency: true,
                    },
                    { inherit: false },
                );
            } catch (error) {
                Sentry.error("[Market Analysis] Failed to navigate to keyword group", error as Error, {
                    tags: { team: "ants" },
                });
            }
        },
        [country, duration],
    );

    const createToastLink = useCallback(
        (keywordGroup: string) => {
            let resolvedCountry = country;

            try {
                resolvedCountry = resolveAllowedCountry(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, country);
            } catch (error) {
                Sentry.error("[Market Analysis] Failed to resolve country for toast link", error as Error, {
                    tags: { team: "ants" },
                });
            }

            const keywordGroupEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId: keywordGroup },
                keywordGroup,
            );

            return proNavigator.href(
                KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                { id: [keywordGroupEntity.keywordListId], country: resolvedCountry, duration },
                { inherit: false },
            );
        },
        [country, duration],
    );

    const columns = useMemo(() => {
        const excludedColumns = new Set(hiddenColumns);

        if (webSource !== devicesTypes.TOTAL) {
            excludedColumns.add(DesktopMobileShare);
        }
        const tableColumns = getColumns(excludedColumns, sort);

        return tableColumns;
    }, [sort, webSource, hiddenColumns]);

    const initialFilters = useMemo(() => getInitialFilters(params, sort), [params, sort]);

    const tableKey = `${TABLE_SELECTION_KEY}-${category?.[0]}`;

    return (
        <SWReactTableWrapperWithSelection
            key={tableKey}
            tableSelectionKey={TABLE_SELECTION_KEY}
            tableSelectionProperty={TABLE_SELECTION_PROPERTY}
            initialFilters={initialFilters}
            transformData={transformData}
            onSort={onSort}
            serverApi={TABLE_ENDPOINT}
            tableColumns={columns}
            tableOptions={{
                noDataTitle: i18n("industry.leaders.table.no.data.title"),
                noDataSubtitle: i18n("industry.leaders.table.no.data.subtitle"),
                tableSelectionTrackingParam: "keyword",
                aboveHeaderComponents: [
                    <AddTableRowsKeywordsToGroupUtility
                        key="1"
                        keywordGroupState={KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE}
                        clearAllSelectedRows={
                            tableActionsCreator(TABLE_SELECTION_KEY, TABLE_SELECTION_PROPERTY).clearAllSelectedRows
                        }
                        stateKey={TABLE_SELECTION_KEY}
                        onViewGroupClick={handleViewKeywordGroupClick}
                        getToastLink={createToastLink}
                    />,
                ],
                metric: "NewSearchKeywords_Table",
            }}
            recordsField="records"
            totalRecordsField="TotalCount"
            pageIndent={1}
            cleanOnUnMount={true}
            rowsPerPage={100}
        >
            {(topComponentProps) => (
                <TableTop
                    {...topComponentProps}
                    params={params}
                    initialFilters={initialFilters}
                    onClickToggleColumns={handleColumnToggle}
                />
            )}
        </SWReactTableWrapperWithSelection>
    );
};
